import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { PageSeo } from './PageSeo';
import { BackToToday, NarrowPage, PageHeader } from './SiteChrome';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';
import { getNewsUnreadCount, NEWS_SEEN_KEY } from '../lib/news';

export function PrivacyPolicyPage() {
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);

  useEffect(() => {
    try {
      setNewsUnreadCount(getNewsUnreadCount(localStorage.getItem(NEWS_SEEN_KEY)));
    } catch {
      setNewsUnreadCount(0);
    }
  }, []);

  return (
    <NarrowPage>
    <div className="relative pb-8">
      <PageSeo meta={PAGE_META.privacy} />
      <PageHeader newsUnreadCount={newsUnreadCount} />

      <motion.div initial={false} animate={{ opacity: 1, y: 0 }}>
        <BackToToday />
        <div className="text-[11px] font-black uppercase tracking-[0.25em] text-foreground/40 mb-3">
          Legal
        </div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-foreground/50 mb-8">Last updated: August 6, 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed text-foreground/80">
          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">Overview</h2>
            <p>
              Centumth (“we”, “us”, or “the Site”) is a daily Top 100 guessing game. This page
              explains what information we collect and how it’s used when you play at{' '}
              <span className="font-semibold text-foreground">centumth.com</span>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">
              Information we collect
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <span className="font-semibold text-foreground">Anonymous play data.</span> Scores,
                guesses, and related game progress may be stored so the game works across sessions
                and so community stats (like guess distributions) can be shown.
              </li>
              <li>
                <span className="font-semibold text-foreground">Local device storage.</span> We use
                your browser’s local storage for things like your anonymous player ID, guesses,
                preferences (theme, arcade settings), medals, and whether you’ve seen news updates.
              </li>
              <li>
                <span className="font-semibold text-foreground">Usage analytics.</span> We use
                privacy-friendly analytics (including Vercel Analytics and Google Analytics) to
                understand aggregate traffic and improve the Site.
              </li>
            </ul>
            <p>
              You do not need an account to play. We do not ask for your name, email, or password to
              use the core game.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">How we use data</h2>
            <p>We use the information above to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Run and improve Centumth (gameplay, stats, and reliability)</li>
              <li>Remember your progress and preferences on your device</li>
              <li>Understand how the Site is used at a high level</li>
              <li>Show advertising through our advertising partner (see below)</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">Advertising</h2>
            <p className="italic text-foreground/85">
              This Site is affiliated with Monumetric (dba for The Blogger Network, LLC) for the
              purposes of placing advertising on the Site, and Monumetric will collect and use
              certain data for advertising purposes. To learn more about Monumetric’s data usage,
              click here:{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.monumetric.com/publisher-advertising-privacy"
                className="font-semibold text-foreground underline underline-offset-2 hover:opacity-80"
              >
                Publisher Advertising Privacy
              </a>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">
              Third-party services
            </h2>
            <p>
              The Site relies on trusted providers to operate, including hosting/analytics (e.g.
              Vercel), database services (Supabase), and advertising (Monumetric). Those providers
              process data according to their own policies in addition to what is described here.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">Your choices</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                You can clear local storage / site data in your browser to reset local progress and
                preferences.
              </li>
              <li>
                You can use browser or device settings to limit cookies and tracking where
                available.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">Contact</h2>
            <p>
              Questions about this policy? Email{' '}
              <a
                href="mailto:eliass.lalaoui@gmail.com"
                className="font-semibold text-foreground underline underline-offset-2 hover:opacity-80"
              >
                eliass.lalaoui@gmail.com
              </a>{' '}
              or use the{' '}
              <a
                href="https://tally.so/r/pbW6Wq"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground underline underline-offset-2 hover:opacity-80"
              >
                Leave Feedback
              </a>{' '}
              form on the Site.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-black tracking-tight text-foreground">Updates</h2>
            <p>
              We may update this Privacy Policy from time to time. The “Last updated” date at the
              top of this page will change when we do.
            </p>
          </section>
        </div>
      </motion.div>

      <SiteFooter />
    </div>
    </NarrowPage>
  );
}
